/**
 * Pure logic for the copilot 'input'-tier value cards (onboarding C2) —
 * seeding the card from the descriptor and validating the owner's value before
 * Confirm is enabled. Extracted from CopilotChat so it's unit-tested without a
 * DOM. Mirrors the server merge contract (arg_names allowlist).
 */
import { BIDDER_PRESETS } from '@auctisync/shared';
import type { InputDescriptor } from '../../hooks/useCopilot';

/** Logo upload gate — MUST match the upload-tenant-logo Edge Function's
 * server-side EXT_BY_MIME + MAX_BYTES (the server re-validates authoritatively;
 * this just avoids a doomed round-trip and gives a friendly inline message).
 * The dropzone uploads the RAW file — logos are deliberately NOT compressed
 * (compressImage re-encodes to JPEG, destroying PNG alpha + SVG vectors). */
export const LOGO_ACCEPT_MIME = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/svg+xml',
] as const;
export const LOGO_MAX_BYTES = 4 * 1024 * 1024;

/**
 * What we ask a house to send, shown ABOVE the upload control.
 *
 * Jim, 2026-09-17: *"We should give them specs and they should provide logos
 * that meet those specs."* Telling them first is the cheap half; the Edge
 * Function enforces it and explains any refusal in its own words.
 *
 * ⚠️ A SECOND STATEMENT of `logo-spec.ts` in `supabase/functions/_shared`, which
 * is authoritative. An Edge Function deploys standalone and cannot import from
 * this workspace, so the numbers cannot be shared — the same arrangement, and
 * the same caveat, as `LOGO_MAX_BYTES` and `LOGO_ACCEPT_MIME` above. If the 5:1
 * ratio or the 80px floor ever move, they move HERE too. The consequence of
 * drift is mild in this direction (we would advertise a spec slightly off the
 * one enforced, and the refusal message would still be correct and specific),
 * which is why it is tolerated rather than solved.
 */
export const LOGO_SPEC_HINT =
  'PNG with a transparent background, or SVG. At least 80 pixels on the shortest side, ' +
  'and no more than 5 times wider than it is tall — a wide banner gets shrunk until ' +
  'your name is hard to read, so send a stacked version if that is all you have.';

/** Validate a picked/dropped logo file client-side. Returns null when OK, else a
 * short human message. Pure (takes only type+size) so it's unit-tested here. */
export function validateLogoFile(file: { type: string; size: number }): string | null {
  if (!(LOGO_ACCEPT_MIME as readonly string[]).includes(file.type)) {
    return 'Use a PNG, JPG, WEBP, GIF, or SVG image.';
  }
  if (file.size === 0) return 'That file is empty.';
  if (file.size > LOGO_MAX_BYTES) return 'Logo must be under 4 MB.';
  return null;
}

const HEX_RE = /^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/;
// hostname: labels + TLD, no protocol/path/port. Mirrors save_tenant_domain.
const DOMAIN_RE = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
const URL_RE = /^https?:\/\/.+/i;

/** Normalize a hex to a 6-digit #rrggbb for the native color swatch, expanding
 * #abc → #aabbcc and adding a missing '#'. Returns null when not a hex, so the
 * caller can fall back to a default (the swatch must always be a full 6-digit
 * value or the native input silently shows black). */
export function expandHexForSwatch(raw: unknown): string | null {
  const m = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(typeof raw === 'string' ? raw.trim() : '');
  const hex = m?.[1];
  if (hex === undefined) return null;
  return hex.length === 3 ? `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}` : `#${hex}`;
}

/** Seed the card's editable value from the descriptor's `initial`, keeping only
 * allowlisted args. The domain card defaults to mode='custom' (that's why the
 * field is shown). */
export function initialCardValue(d: InputDescriptor): Record<string, unknown> {
  const init = d.initial ?? {};
  const out: Record<string, unknown> = {};
  for (const k of d.arg_names) if (init[k] !== undefined) out[k] = init[k];
  if (d.kind === 'domain' && out['mode'] === undefined) out['mode'] = 'custom';
  return out;
}

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** Whether the owner's value is complete enough to Confirm. The server tool
 * re-validates, but gating here avoids a round-trip on obviously-incomplete
 * input. */
export function isCardValueComplete(d: InputDescriptor, v: Record<string, unknown>): boolean {
  switch (d.kind) {
    case 'branding': {
      const primary = str(v['primary_color']);
      const accent = str(v['accent_color']);
      const dark = str(v['dark_color']);
      const logo = str(v['logo_url']);
      if (!HEX_RE.test(primary)) return false;
      if (accent !== '' && !HEX_RE.test(accent)) return false;
      // Same shape as the accent: optional, but a value that IS given has to be
      // a real hex. The server refuses it too — this only saves the round-trip.
      if (dark !== '' && !HEX_RE.test(dark)) return false;
      if (logo !== '' && !URL_RE.test(logo)) return false;
      // The preset is optional — a house that says nothing gets `modern`. Only
      // a value we do not recognise blocks Confirm, and the server refuses it
      // too: an unknown preset renders an unstyled page rather than an error.
      const preset = str(v['preset']);
      if (preset !== '' && !BIDDER_PRESETS.includes(preset as (typeof BIDDER_PRESETS)[number]))
        return false;
      return true;
    }
    case 'domain':
      return DOMAIN_RE.test(str(v['domain']).toLowerCase());
    case 'view_selector':
      return str(v['view']) !== '';
    default:
      return false;
  }
}

export const __test = { HEX_RE, DOMAIN_RE };
